/**
 * Format a CompiledRecipe's action_args + post_processing into a flat list
 * of human-readable {key, value} chips. Powers the "Parameters" row in the
 * interpretation card and the Field-comparison panel in the diagnostic
 * dialog. Always returns the recipe's actual state so users can spot a
 * drifted LLM translation (e.g. prompt asked for "sessions" but the
 * recipe is querying "activeUsers") before saving.
 *
 * Toolkit-aware: we know GA4, HubSpot, Stripe, Linear, GitHub, and Google
 * Sheets well enough to extract their high-signal args. Everything else
 * falls through to a generic top-level dump.
 */
export function formatRecipeParams(recipe, toolkitSlug) {
  if (!recipe) return []
  const args = recipe.action_args || {}
  const pp = recipe.post_processing || {}
  const slug = (toolkitSlug || '').toLowerCase()

  const rows = []
  const push = (key, value) => {
    if (value === undefined || value === null || value === '') return
    rows.push({ key, value: String(value) })
  }

  switch (slug) {
    case 'google_analytics': {
      // GA4 batchRunReports: metrics[], dimensions[], dimensionFilter,
      // dateRanges, property. The metric is the most diagnostic field
      // since LLM drift here is the typical bug pattern.
      const metrics = arrayOrEmpty(args.metrics)
        .map((m) => (typeof m === 'string' ? m : (m && m.name) || ''))
        .filter(Boolean)
      if (metrics.length) push('Metric', metrics.join(', '))
      const dims = arrayOrEmpty(args.dimensions)
        .map((d) => (typeof d === 'string' ? d : (d && d.name) || ''))
        .filter(Boolean)
      if (dims.length) push('Dimensions', dims.join(', '))
      push('Property', args.property || args.propertyId || args.property_id)
      const dr = args.dateRange || args.dateRanges
      const drFmt = formatGaDateRange(dr)
      if (drFmt) push('Date range', drFmt)
      const filt = describeGaFilter(args.dimensionFilter)
      if (filt) push('Filter', filt)
      break
    }
    case 'hubspot': {
      const filt = describeHubspotFilters(args.filterGroups)
      if (filt) push('Filter', filt)
      const props = arrayOrEmpty(args.properties).filter(Boolean)
      if (props.length) push('Properties', props.join(', '))
      if (args.limit) push('Limit', args.limit)
      break
    }
    case 'stripe': {
      if (args.status) push('Status', args.status)
      if (args.limit) push('Limit', args.limit)
      break
    }
    case 'linear': {
      if (args.state) push('State', args.state)
      if (args.team) push('Team', args.team)
      if (args.assignee) push('Assignee', args.assignee)
      if (args.limit) push('Limit', args.limit)
      break
    }
    case 'github': {
      if (args.state) push('State', args.state)
      if (args.repo || args.repository) push('Repo', args.repo || args.repository)
      if (args.per_page) push('Limit', args.per_page)
      break
    }
    case 'google_sheets': {
      if (args.range) push('Range', args.range)
      if (args.spreadsheetId) push('Spreadsheet', args.spreadsheetId)
      break
    }
    default: {
      // Generic: dump top-level scalar args as-is so even unknown toolkits
      // expose what the recipe is configured to fetch.
      Object.keys(args).forEach((k) => {
        const v = args[k]
        if (v == null) return
        if (Array.isArray(v)) {
          if (v.length && v.every((x) => typeof x === 'string' || typeof x === 'number')) {
            push(prettifyKey(k), v.join(', '))
          }
        } else if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          push(prettifyKey(k), v)
        }
      })
    }
  }

  // Post-processing summary: always include the aggregation + value_field
  // so users can see what's being summed/counted across whatever rows the
  // action returns.
  if (pp.aggregation) {
    const aggLabel = pp.aggregation.charAt(0).toUpperCase() + pp.aggregation.slice(1)
    if (pp.value_field) push('Aggregate', `${aggLabel} of ${pp.value_field}`)
    else push('Aggregate', aggLabel)
  }

  return rows
}

/**
 * Detect drift between the user's prompt and the recipe's action_args.
 * Currently focused on GA4 metric names because that's where users
 * naturally write a metric word (sessions, visitors, page views, etc.)
 * and the LLM frequently picks a different metric. Returns null when
 * there is no drift to report.
 *
 * Returns: { mentionedMetric, recipeMetric } when drift detected.
 */
export function detectPromptRecipeDrift(prompt, recipe, toolkitSlug) {
  if (!prompt || !recipe) return null
  const slug = (toolkitSlug || '').toLowerCase()
  if (slug !== 'google_analytics') return null

  const lower = prompt.toLowerCase()
  // Map of natural-language keywords → canonical GA4 metric names.
  // Order matters: 'active users' must beat 'users'.
  const dict = [
    { kw: 'active users', metric: 'activeUsers' },
    { kw: 'new users', metric: 'newUsers' },
    { kw: 'total users', metric: 'totalUsers' },
    { kw: 'users', metric: 'activeUsers' },
    { kw: 'visitors', metric: 'totalUsers' },
    { kw: 'sessions', metric: 'sessions' },
    { kw: 'session', metric: 'sessions' },
    { kw: 'page views', metric: 'screenPageViews' },
    { kw: 'pageviews', metric: 'screenPageViews' },
    { kw: 'screen views', metric: 'screenPageViews' },
    { kw: 'events', metric: 'eventCount' },
    { kw: 'event count', metric: 'eventCount' },
    { kw: 'conversions', metric: 'conversions' },
    { kw: 'bounce rate', metric: 'bounceRate' },
  ]
  let mentioned = null
  for (const entry of dict) {
    if (lower.includes(entry.kw)) {
      mentioned = entry
      break
    }
  }
  if (!mentioned) return null

  const args = recipe.action_args || {}
  const recipeMetrics = arrayOrEmpty(args.metrics)
    .map((m) => (typeof m === 'string' ? m : (m && m.name) || ''))
    .filter(Boolean)
  if (recipeMetrics.length === 0) return null

  // Drift if the canonical metric the user mentioned isn't in the recipe.
  const recipeLower = recipeMetrics.map((m) => m.toLowerCase())
  if (recipeLower.includes(mentioned.metric.toLowerCase())) return null
  // Also accept partial matches (e.g. "totalUsers" satisfies "users").
  if (recipeLower.some((m) => m.includes(mentioned.metric.toLowerCase()) || mentioned.metric.toLowerCase().includes(m))) return null

  return {
    mentionedKeyword: mentioned.kw,
    suggestedMetric: mentioned.metric,
    recipeMetric: recipeMetrics.join(', '),
  }
}

/* ---------- internal helpers ---------- */

function arrayOrEmpty(v) {
  return Array.isArray(v) ? v : []
}

function prettifyKey(k) {
  return String(k)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
}

function formatGaDateRange(dr) {
  if (!dr) return ''
  // dateRanges is an array of { startDate, endDate }; dateRange is a single obj.
  const range = Array.isArray(dr) ? dr[0] : dr
  if (!range) return ''
  const start = range.startDate || range.start_date || range.start
  const end = range.endDate || range.end_date || range.end
  if (!start && !end) return ''
  // GA4 uses tokens like "30daysAgo" — keep as-is when natural-language.
  return `${start || '?'} → ${end || 'today'}`
}

function describeGaFilter(df) {
  if (!df) return ''
  // GA4 dimensionFilter is a tree; we only show a one-liner for the
  // simplest cases. Anything complex falls back to a generic indicator
  // so users know a filter exists.
  if (df.filter && df.filter.fieldName) {
    const f = df.filter
    if (f.stringFilter && f.stringFilter.value) {
      const op = (f.stringFilter.matchType || 'EXACT').toLowerCase()
      return `${f.fieldName} ${op} "${f.stringFilter.value}"`
    }
    return f.fieldName
  }
  if (df.andGroup || df.orGroup) return 'compound filter (see backend)'
  return ''
}

function describeHubspotFilters(groups) {
  if (!Array.isArray(groups) || !groups.length) return ''
  const first = groups[0]
  if (!first || !Array.isArray(first.filters) || !first.filters.length) return ''
  const parts = first.filters.slice(0, 2).map((f) => {
    const op = (f.operator || 'EQ').toLowerCase()
    return `${f.propertyName} ${op} ${f.value}`
  })
  return parts.join(', ') + (first.filters.length > 2 ? '…' : '')
}
